import { http } from '@/lib/ats-https';
import { http as httpApiV1 } from '@/lib/http';
import { PAGINATION } from '@/constants/pagination';
import { API_ENDPOINTS, CONFIG } from './quickAppReview.constants';

// Helper function to get localStorage sort preference
const getStoredSort = (storageKey) => {
  if (!storageKey) return null;

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const markAsViewedApplicationV1 = async (id) => {
  return http.post(API_ENDPOINTS.ENTITY_VIEWS, {
    viewable_id: id,
    viewable_type: CONFIG.PAYLOAD.VIEWABLE_TYPE,
  });
};

/**
 * @param {string|number} applicationId
 * @param {{ include?: string[] }} [options] - e.g. { include: ['stage_actions'] } sends include[]=stage_actions
 */
const getApplicationDetails = async (applicationId, options) => {
  const include = options?.include;
  const params = include?.length > 0 ? { 'include[]': include } : undefined;
  const response = await http.get(
    API_ENDPOINTS.APPLICATION_DETAILS(applicationId),
    { params }
  );
  return response?.data || response;
};

const getApplicationNotes = async (applicationId, params = {}) => {
  const {
    page = CONFIG.NOTES.DEFAULT_PAGE,
    items = CONFIG.NOTES.DEFAULT_ITEMS,
  } = params;
  const response = await http.get(
    API_ENDPOINTS.APPLICATION_NOTES(applicationId),
    {
      params: {
        page,
        items,
      },
    }
  );
  // Return the full response including both data and meta
  // The response structure is typically { data: [...notes], meta: { count, page, pages, ... } }
  return response;
};

/**
 * Fetches contact details by id. Matches the shape expected by ContactDetails
 * (same as getContactById transform: the contact object).
 * API: GET /api/v1/contacts/{contactId}
 */
const getContactDetails = async (contactId) => {
  const response = await httpApiV1.get(
    API_ENDPOINTS.CONTACT_DETAILS(contactId)
  );
  const body = response?.data ?? response;
  return body?.contact ?? body?.data?.contact ?? body;
};

/**
 * Fetches the next page of applications (e.g. when user reaches end of current list).
 * Used for pagination in Quick App Review. Payload should include offset/scroll_token
 * when fetching next page (from buildFetchApplicationsPayload with resetPagination: false).
 */
const fetchNextPageApplications = async (jobId, payload) => {
  const response = await http({
    method: 'post',
    url: API_ENDPOINTS.SEARCH_APPLICATIONS(jobId),
    data: payload,
  });
  return response?.data || response;
};

const buildFetchApplicationsPayload = ({
  appReviewStageId,
  sort,
  currentTab,
  is_ai_review_failed_applied,
  filterKeys,
  resume_text,
  isAIScoreEnabled,
  globalSortStorageKey,
  sortFromParam,
  skipViewed,
  resetPagination,
  page,
}) => {
  const payload = {
    stage_ids: [appReviewStageId],
    sort_key: sort?.element,
    sort_order: sort?.order,
    status: CONFIG.PAYLOAD.STATUS,
    limit: PAGINATION.APPLICATION,
    quick_review: CONFIG.PAYLOAD.QUICK_REVIEW,
  };

  if (skipViewed) {
    payload['skip_viewed'] = true;
  }
  if (currentTab === 'candidates') {
    if (is_ai_review_failed_applied) payload['ai_review_status'] = 'failed';
    Object.entries(filterKeys).forEach(([key, value]) => {
      payload[key] = value;
    });
    // Handle resume_text filter structure
    if (resume_text) {
      const { resume_text_and, resume_text_or } = resume_text;
      if (resume_text_and) {
        if (typeof resume_text_and === 'string') {
          payload['resume_text'] = { and: [resume_text_and] };
        } else {
          payload['resume_text'] = {
            and: [resume_text.resume_text_and.join(' ')],
          };
        }
      } else if (resume_text_or) {
        if (typeof resume_text_or === 'string') {
          payload['resume_text'] = { or: [resume_text_or] };
        } else {
          payload['resume_text'] = {
            or: [resume_text.resume_text_or.join(' ')],
          };
        }
      }
    }
  }

  // Apply sort logic only for pipeline tab
  if (currentTab === 'pipeline') {
    if (!isAIScoreEnabled) {
      // AI scoring disabled - use global sort localStorage or fallback to reducer
      const globalStoredSort = getStoredSort(globalSortStorageKey);
      if (globalStoredSort) {
        payload['sort_key'] = globalStoredSort.element;
        payload['sort_order'] = globalStoredSort.order;
      }
    } else {
      // If AI scoring is enabled, use the sort from the param
      payload['sort_key'] = sortFromParam?.element;
      payload['sort_order'] = sortFromParam?.order;
      // If page sort is selected, use global sort
      if (sortFromParam?.value === CONFIG.PAYLOAD.PAGE_SORT_VALUE) {
        const globalStoredSort = getStoredSort(globalSortStorageKey);
        const sortToUse = globalStoredSort || sort;
        payload['sort_key'] = sortToUse?.element;
        payload['sort_order'] = sortToUse?.order;
      }
    }
  }

  // Only include pagination parameters if not resetting
  if (!resetPagination) {
    // Include offset if it's defined (including 0, which is a valid offset)
    if (page?.offset !== undefined && page?.offset !== null) {
      payload['offset'] = page?.offset;
    }
    // Include scroll_token if it exists and is not empty
    if (page?.scroll_token) {
      payload['scroll_token'] = page?.scroll_token;
    }
  }

  return payload;
};

export const QUICK_APP_REVIEW_V1_API_SERVICES = {
  markAsViewedApplicationV1,
  buildFetchApplicationsPayload,
  getApplicationDetails,
  getApplicationNotes,
  getContactDetails,
  fetchNextPageApplications,
};
